import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Alert,
  Spinner,
} from "reactstrap"
import { Link, useNavigate } from "react-router-dom"

const SubRequestsSection = ({ parentRequest, myUser, onSubRequestCreated }) => {
  const navigate = useNavigate()
  const [subRequests, setSubRequests] = useState([])
  const [loading, setLoading] = useState(true)

  // Load sub-requests from parent request data
  useEffect(() => {
    loadSubRequests()
  }, [parentRequest?.subRequests])

  const loadSubRequests = () => {
    if (!parentRequest?.subRequests) {
      setSubRequests([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      // Transform parent request subRequests data to frontend format
      const transformedSubRequests = parentRequest.subRequests.map(
        subRequest => ({
          id: subRequest.id,
          requestNumber: subRequest.requestNumber,
          parentRequestNumber: subRequest.parentRequestNumber,
          quantity: subRequest.quantity,
          deliveryDate: subRequest.desiredDeliveryDate
            ? subRequest.desiredDeliveryDate.split("T")[0]
            : "",
          status: mapStatusIndex(subRequest.statusIndex),
          dateCreated: subRequest.dateCreated
            ? subRequest.dateCreated.split("T")[0]
            : "",
          isLocked: false, // This would need to be added to the API
          notes: subRequest.notes || "",
        })
      )
      setSubRequests(transformedSubRequests)
    } catch (error) {
      console.error("Error processing sub-requests:", error)
      setSubRequests([])
    }
    setLoading(false)
  }

  const mapStatusIndex = statusIndex => {
    const statusMap = {
      1: "Request Created",
      2: "CPS Offer",
      3: "Customer Decision",
      4: "Production",
      5: "Completed",
      6: "Cancelled",
      7: "Sample Production",
      8: "Sample Approval",
      9: "Production",
      10: "Completed",
    }
    return statusMap[statusIndex] || "Unknown"
  }

  const handleCreateSubRequest = () => {
    // Navigate to request creation with pre-filled data
    const subRequestData = {
      type: "Programming", // Fixed type
      needType: "one-time", // Fixed need type
      parentID: parentRequest.id, // Parent ID for linking
      parentRequestId: parentRequest.id, // Keep for backward compatibility
      parentRequestNumber: parentRequest.requestNumber,
      articleDescription: parentRequest.articleDescription,
      mpn: parentRequest.mpn,
      manufacturer: parentRequest.manufacturer,
      packageType: parentRequest.packageType,
      deliveryForm: parentRequest.deliveryForm,
      supplyForm: parentRequest.supplyForm,
      quantity: "", // User will fill this in
      desiredDeliveryDate: "", // User will fill this in
      instructions: parentRequest.instructions,
      notes: "", // User can add notes
      customerSupply: parentRequest.customerSupply,
      supplier: parentRequest.supplier,
      sampleRequired: parentRequest.sampleRequired,
      sampleQuantity: parentRequest.sampleQuantity,
      samplePrice: parentRequest.samplePrice,
    }

    // Store sub-request data in sessionStorage for the request creation page
    sessionStorage.setItem("subRequestData", JSON.stringify(subRequestData))

    // Navigate to request creation
    navigate("/cps-request-wizard")
  }

  const toggleLock = subRequestId => {
    setSubRequests(
      subRequests.map(req =>
        req.id === subRequestId ? { ...req, isLocked: !req.isLocked } : req
      )
    )
  }

  const getStatusBadgeColor = status => {
    const colors = {
      "Request Created": "secondary",
      "CPS Offer": "primary",
      Production: "success",
      Completed: "info",
      Cancelled: "danger",
    }
    return colors[status] || "secondary"
  }

  const canManageSubRequests =
    myUser?.company?.id === parentRequest?.requestorCompanyID

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Sub-Requests (Production Batches)</h5>
          {canManageSubRequests && (
            <Button color="primary" size="sm" onClick={handleCreateSubRequest}>
              <i className="bx bx-plus me-1"></i>
              Create Sub-Request
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading sub-requests...</div>
          </div>
        ) : subRequests.length === 0 ? (
          <Alert color="info">
            No sub-requests created yet. Create the first sub-request to manage
            production batches for this annual request.
          </Alert>
        ) : (
          <div className="table-responsive">
            <Table className="table-nowrap mb-0">
              <thead className="table-light">
                <tr>
                  <th>Request #</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subRequests.map(subRequest => (
                  <tr key={subRequest.id}>
                    <td>
                      <Link
                        to={`/cps-requests/${subRequest.id}`}
                        className="text-body fw-bold"
                      >
                        {subRequest.requestNumber}
                      </Link>
                    </td>
                    <td>{subRequest.quantity?.toLocaleString()} pcs</td>
                    <td>
                      <Badge color={getStatusBadgeColor(subRequest.status)}>
                        {subRequest.status}
                      </Badge>
                    </td>
                    <td>{subRequest.dateCreated}</td>

                    <td>
                      <div className="d-flex gap-1">
                        <Link
                          to={`/cps-requests/${subRequest.id}`}
                          className="btn btn-sm btn-outline-primary"
                          title="View Details"
                        >
                          <i className="bx bx-show"></i>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default SubRequestsSection
